import * as styles from './multiple-choice-panel.css';

import Content from '@nrdy-marketing-engine/components/src/content';
import { useInterpolation } from '@nrdy-marketing-engine/interpolation';
import classNames from 'classnames';
import { useCallback } from 'react';

import useDomTracking from '@nrdy-marketing-engine/tracking/src/use-dom-tracking';
import clsx from 'clsx';
import { MutipleChoicePanelData, PanelStateProps } from '../../@types/panel-data';
import BackButton from '../back-button';
import CheckmarkIcon from '../checkmark';
import ContinueButton from '../continue-button';
import InputValidation from '../form-input/input-validation';
import PanelButton from '../panel-button';
import { MultipleChoiceOptions, MultipleChoiceOptionsProps } from './types';

const isChecked = (inputValue: string | string[], key: string) =>
  Array.isArray(inputValue) && inputValue.includes(key);

/** Allows the system to interpret the different ways that choices can be given. */
export function convertChoices(choices: MultipleChoiceOptions): { text: string; value: string }[] {
  if (!Array.isArray(choices)) {
    return Object.entries(choices).map(([value, text]) => ({ value, text }));
  } else {
    if (typeof choices[0] === 'string') {
      return choices.map((choice) => ({ value: choice, text: choice }));
    } else {
      return choices.map((choice) => {
        const text = 'key' in choice ? choice.key : choice.text;
        return { value: choice.value, text };
      });
    }
  }
}

interface CheckboxOptionProps {
  checkboxClass: (checked: boolean) => string;
  inputValue: string | string[];
  name: string;
  onChoose: (value: string) => void;
  onNext: () => void;
  text: string;
  value: string;
}

/**
 * Renders a single checkbox option.
 */
const CheckboxOption = ({
  checkboxClass,
  name,
  value,
  text,
  onChoose,
  onNext,
  inputValue,
}: CheckboxOptionProps) => {
  // Override the default dom arguments to match legacy events
  const blurTrackable = useDomTracking('blur', {
    target: 'INPUT',
    name,
    value,
  });
  const clickTrackable = useDomTracking('click');

  // If the user presses Enter, attempt to move to the next input
  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        onNext();
      }
    },
    [onNext]
  );

  return (
    <label ref={clickTrackable}>
      <button
        className="flex items-center hover:text-link"
        key={value}
        onClick={() => onChoose(value)}
        onKeyDown={handleKeyDown}
        ref={blurTrackable}
      >
        <span className={checkboxClass(isChecked(inputValue, value))} />
        <span className={`text-xl mt-[1px] ${isChecked(inputValue, value) ? 'font-semibold' : ''}`}>
          {text}
        </span>
      </button>
    </label>
  );
};

/**
 * Renders the component with a checkbox for each choice, multiple items can be selected.
 */
function CheckboxOptions({
  choices,
  onChoose,
  inputValue,
  name,
  onNext,
}: MultipleChoiceOptionsProps) {
  const checkboxClass = (checked: boolean) =>
    classNames('mr-2 w-5 h-5 border-2 border-primary rounded-sm transition-all', {
      ['bg-primary relative checkmark']: checked,
      ['bg-white']: !checked,
    });

  return (
    <div className={styles.optionsContainer}>
      <div className="md:min-w-[300px]">
        {convertChoices(choices).map(({ value, text }) => {
          return (
            <CheckboxOption
              checkboxClass={checkboxClass}
              inputValue={inputValue}
              key={value}
              name={name}
              onChoose={onChoose}
              onNext={onNext}
              text={text}
              value={value}
            />
          );
        })}
      </div>
    </div>
  );
}

/**
 * Renders the component with a button for each choice, only one can be selected.
 */
function ChoiceOptions({ choices, onChoose, nmeCentered, inputValue }: MultipleChoiceOptionsProps) {
  const classes = clsx(styles.optionsContainer, nmeCentered && styles.centeredLayout);

  return (
    <div className={classes}>
      {convertChoices(choices).map(({ value, text }) => {
        const ChoiceOptionButton = (key) => {
          const interpolatedText = useInterpolation(text);

          return (
            <PanelButton
              className={styles.button}
              key={key}
              onClick={() => onChoose(value)}
              text={interpolatedText}
            />
          );
        };
        return (
          <div className={styles.choiceContainer} key={value}>
            <ChoiceOptionButton />
            <CheckmarkIcon showIcon={inputValue === value} />
          </div>
        );
      })}
    </div>
  );
}

/**
 * Performs the necessary update logic when choosing an option, based on whether we are in
 * checkbox mode or not, and returns the new value of the control.
 */
function updateChoice(choice: string, checkboxes: boolean, currentValue: string[] | string) {
  // for a simple control, the new value is what we picked
  // if the existing value is the wrong format, the new value is what we picked
  if (!checkboxes) return choice;
  if (!Array.isArray(currentValue)) return [choice];

  if (currentValue.includes(choice)) {
    // the new value is removing the given choice from the array
    return currentValue.filter((v) => v != choice);
  } else {
    // the new value is adding the given choice to the array
    return [...currentValue, choice];
  }
}

/**
 * A form panel that represents a selection of choices, with or without checkboxes.
 */
function MultipleChoicePanel({
  name,
  headline,
  content,
  checkboxes,
  onNext,
  onBack,
  onInput,
  continueButton,
  nmeCentered = false,
  backButton,
  formData,
  choices,
  loading,
  validationErrors,
}: MutipleChoicePanelData & PanelStateProps) {
  const currentValue = formData[name];

  /**
   * Handles the user clicking on a choice, sending the `onInput` callback to tell the
   * form to update the field value accordingly.
   */
  async function handleChoice(choice) {
    const newValue = updateChoice(choice, checkboxes, currentValue);
    onInput({ [name]: newValue });
    formData[name] = newValue;
    if (!checkboxes) {
      await new Promise((resolve) => setTimeout(resolve, 300));
      onNext();
    }
  }

  const contentClasses = `prose-base prose-strong:text-xl sm:prose-strong:text-2xl sm:prose-lg ${
    nmeCentered ? 'text-center' : ''
  }`;

  let showBackButton = true;

  if (backButton && Array.isArray(backButton) && backButton.length === 0) {
    showBackButton = false;
  }

  const panelLayout = (
    <>
      <Content
        className={clsx(contentClasses, styles.heading)}
        heading={useInterpolation(headline)}
        headingLevel={1}
      >
        {useInterpolation(content)}
      </Content>
      {checkboxes ? (
        <>
          <CheckboxOptions
            choices={choices}
            inputValue={currentValue}
            name={name}
            onChoose={handleChoice}
            onNext={onNext}
          />
          <InputValidation validationErrors={validationErrors.custom} />
          <ContinueButton loading={loading} onClick={onNext} recenter text={continueButton?.text} />
        </>
      ) : (
        <>
          <ChoiceOptions
            choices={choices}
            inputValue={currentValue}
            nmeCentered={nmeCentered}
            onChoose={handleChoice}
          />
          <InputValidation validationErrors={validationErrors.custom} />
        </>
      )}
      {showBackButton && <BackButton onClick={onBack} text={backButton.text} />}
    </>
  );

  return (
    <div className={clsx(styles.layout, nmeCentered && styles.centeredLayout)}>{panelLayout}</div>
  );
}

export default MultipleChoicePanel;
